"use client";

import React, { useCallback } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import {
  Checkbox,
  Radio,
  Select,
  Slider,
  Text,
  timerFormatterFactory,
} from "@lmn/design-system";
import IconFlightChange from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconFlightChange.js";
import IconTrain from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconTrain.js";
import IconCircleInfo from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconCircleInfo.js";
import initialState from "@/lib/StaticData/searchFormData";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import ResponsiveTooltip from "@/components/UI/Tooltip/ResponsiveTooltip";

const FilterWrapper = styled.div`
  width: 100%;
  margin-top: 1rem;
`;

const CheckboxWrapper = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const SelectWrapper = styled.div`
  margin-top: 1rem;
`;

const RadioWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const RadioGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  z-index: 0;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
`;

const SliderLabeltext = styled.p`
  font-weight: 400;
  font-size: 14px;
  lne-height: 20px;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const SliderWrapper = styled.div`
  & > div {
    background-color: transparent !important;
  }
  & [dir="ltr"] > span:first-child {
    background-color: ${({ theme }) =>
    theme.kallisto.color.background.static.backgroundOverlay} !important;
  }
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 1rem 0;
  background-color: ${({ theme }) =>
    theme.kallisto.color.background.static.backgroundDisabled};
`;

interface TransferFilterProps {
  delayedUpdate?: boolean;
  isHotelDetailPage?: boolean;
  expanded?: boolean;
}

const TransferFilter: React.FC<TransferFilterProps> = ({
  delayedUpdate = false,
  isHotelDetailPage = false,
  expanded = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilter } = useFilterState();
  const transfer = initialState.transfer;

  // Initialize states from either URL params or temp filters based on delayedUpdate
  const departureFlightTimeFrom = delayedUpdate
    ? Number(tempFilters.get("departureFlightTimeFrom")) || 0
    : Number(searchParams.get("departureFlightTimeFrom")) || 0;

  const departureFlightTimeUntil = delayedUpdate
    ? Number(tempFilters.get("departureFlightTimeUntil")) || 1440
    : Number(searchParams.get("departureFlightTimeUntil")) || 1440;

  const returnFlightTimeFrom = delayedUpdate
    ? Number(tempFilters.get("returnFlightTimeFrom")) || 0
    : Number(searchParams.get("returnFlightTimeFrom")) || 0;

  const returnFlightTimeUntil = delayedUpdate
    ? Number(tempFilters.get("returnFlightTimeUntil")) || 1440
    : Number(searchParams.get("returnFlightTimeUntil")) || 1440;

  // Derive state directly from URL/tempFilters - no local state needed
  const isDirectFlight = delayedUpdate
    ? tempFilters.get("directFlight") === "1"
    : searchParams.get("directFlight") === "1";

  const isRailAndFly = delayedUpdate
    ? tempFilters.get("railAndFly") === "1"
    : searchParams.get("railAndFly") === "1";

  // Derive selectedTransfer from URL/tempFilters
  const getSelectedTransfer = (): string => {
    if (delayedUpdate) {
      if (tempFilters.get("transfer") === "1") return "transfer";
      if (tempFilters.get("rentalCar") === "1") return "rentalCar";
      return "anything";
    }
    if (searchParams.get("transfer") === "1") return "transfer";
    if (searchParams.get("rentalCar") === "1") return "rentalCar";
    return "anything";
  };
  const selectedTransfer = getSelectedTransfer();

  // Handlers for flight time sliders
  const handleDepartureFlightTimeChange = useCallback(
    (value: number | number[]) => {
      if (!Array.isArray(value)) return;
      const [from, until] = value;

      if (delayedUpdate) {
        updateTempFilter(
          "departureFlightTimeFrom",
          from === 0 ? null : String(from)
        );
        updateTempFilter("departureFlightTimeUntil", String(until));
      } else {
        const current = new URLSearchParams(searchParams.toString());

        if (from === 0) {
          current.delete("departureFlightTimeFrom");
        } else {
          current.set("departureFlightTimeFrom", String(from));
        }
        current.set("departureFlightTimeUntil", String(until));

        window.history.pushState(
          {},
          "",
          `${window.location.pathname}?${current.toString()}`
        );
        window.dispatchEvent(new Event("urlchange"));
      }
    },
    [delayedUpdate, updateTempFilter, searchParams]
  );

  const handleReturnFlightTimeChange = useCallback(
    (value: number | number[]) => {
      if (!Array.isArray(value)) return;
      const [from, until] = value;

      if (delayedUpdate) {
        updateTempFilter(
          "returnFlightTimeFrom",
          from === 0 ? null : String(from)
        );
        updateTempFilter("returnFlightTimeUntil", String(until));
      } else {
        const current = new URLSearchParams(searchParams.toString());

        if (from === 0) {
          current.delete("returnFlightTimeFrom");
        } else {
          current.set("returnFlightTimeFrom", String(from));
        }
        current.set("returnFlightTimeUntil", String(until));

        window.history.pushState(
          {},
          "",
          `${window.location.pathname}?${current.toString()}`
        );
        window.dispatchEvent(new Event("urlchange"));
      }
    },
    [delayedUpdate, updateTempFilter, searchParams]
  );

  const handleDirectFlightChange = useCallback(() => {
    const newValue = !isDirectFlight;
    if (delayedUpdate) {
      updateTempFilter("directFlight", newValue ? "1" : null);
    } else {
      updateFilter("directFlight", newValue ? "1" : null);
    }
  }, [isDirectFlight, updateFilter, updateTempFilter, delayedUpdate]);

  const handleRailAndFlyChange = useCallback(() => {
    const newValue = !isRailAndFly;
    if (delayedUpdate) {
      updateTempFilter("railAndFly", newValue ? "1" : null);
    } else {
      updateFilter("railAndFly", newValue ? "1" : null);
    }
  }, [isRailAndFly, updateFilter, updateTempFilter, delayedUpdate]);

  const handleTransferChange = useCallback(
    (selected: string) => {
      if (delayedUpdate) {
        // Only call updateTempFilter once since it has mutual exclusivity logic
        // that clears both transfer and rentalCar before setting the new value
        if (selected === "transfer") {
          updateTempFilter("transfer", "1");
        } else if (selected === "rentalCar") {
          updateTempFilter("rentalCar", "1");
        } else {
          // "anything" - clear both by setting transfer to null
          updateTempFilter("transfer", null);
        }
      } else {
        // Update both params via pushState for instant response
        const current = new URLSearchParams(searchParams.toString());
        current.delete("transfer");
        current.delete("rentalCar");

        if (selected === "transfer") {
          current.set("transfer", "1");
        } else if (selected === "rentalCar") {
          current.set("rentalCar", "1");
        }

        window.history.pushState(
          {},
          "",
          `${window.location.pathname}?${current.toString()}`
        );
        window.dispatchEvent(new Event("urlchange"));
      }
    },
    [updateTempFilter, delayedUpdate, searchParams]
  );

  const options = transfer.allIds.map((id) => ({
    value: id,
    label: transfer.byId[id].label,
  }));

  return (
    <FilterWrapper data-testid="transferFilter">
      {isHotelDetailPage && (
        <SliderWrapper>
          <Slider
            LabelText={() => (
              <SliderLabeltext>Abflugzeit Hinreise</SliderLabeltext>
            )}
            rangeMin={0}
            rangeMax={1440}
            step={5}
            selectedValue={departureFlightTimeFrom}
            selectedSecondValue={departureFlightTimeUntil}
            formatter={timerFormatterFactory({
              hours: "h",
              minutes: "m",
              divider: " : ",
            })}
            handleCommittedChange={handleDepartureFlightTimeChange}
          />

          <Slider
            LabelText={() => (
              <SliderLabeltext>Abflugzeit Rückreise</SliderLabeltext>
            )}
            rangeMin={0}
            rangeMax={1440}
            step={5}
            selectedValue={returnFlightTimeFrom}
            selectedSecondValue={returnFlightTimeUntil}
            formatter={timerFormatterFactory({
              hours: "h",
              minutes: "m",
              divider: " : ",
            })}
            handleCommittedChange={handleReturnFlightTimeChange}
          />
        </SliderWrapper>
      )}

      {expanded && <Divider />}

      {/* Checkboxes with ClickOverlay for delayed update mode */}
      <CheckboxWrapper data-testid="directFlight-wrapper">
        {delayedUpdate && <ClickOverlay onClick={handleDirectFlightChange} />}
        <IconFlightChange size={24} />
        <Checkbox
          checkbox={{
            id: "directFlight",
            label: (
              <Label>
                <Text variant="BodySmall">Direktflug</Text>
                <ResponsiveTooltip
                  content="Mit diesem Filter erhalten sie fast ausschließlich Angebote mit Direktflügen. Im Gegensatz zu Non-Stop Flügen, können Direktflüge mit einer Flugnummer auch Zwischenlandungen beinhalten."
                  trigger={<IconCircleInfo size={14} />}
                />
              </Label>
            ),
          }}
          isChecked={isDirectFlight}
          onChange={delayedUpdate ? () => { } : handleDirectFlightChange}
        />
      </CheckboxWrapper>

      <CheckboxWrapper data-testid="railAndFly-wrapper">
        {delayedUpdate && <ClickOverlay onClick={handleRailAndFlyChange} />}
        <IconTrain size={24} />
        <Checkbox
          checkbox={{
            id: "railAndFly",
            label: "Zug zum Flughafen",
          }}
          isChecked={isRailAndFly}
          onChange={delayedUpdate ? () => { } : handleRailAndFlyChange}
        />
      </CheckboxWrapper>

      {expanded ? (
        <>
          <Divider />
          <RadioGroupContainer>
            {options.map((option) => (
              <RadioWrapper key={option.value}>
                {delayedUpdate && (
                  <ClickOverlay
                    onClick={() => handleTransferChange(option.value)}
                  />
                )}
                <Radio
                  name="transfer"
                  checked={selectedTransfer === option.value}
                  onChange={
                    delayedUpdate
                      ? () => { }
                      : () => handleTransferChange(option.value)
                  }
                />
                <Text variant="BodySmall">{option.label}</Text>
              </RadioWrapper>
            ))}
          </RadioGroupContainer>{" "}
        </>
      ) : (
        <SelectWrapper>
          <Select
            value={selectedTransfer}
            onChange={handleTransferChange}
            label="Transferleistung"
            name="select1"
            isFullWidth
            className="asn-select"
            options={options}
          />
        </SelectWrapper>
      )}
    </FilterWrapper>
  );
};

export default TransferFilter;
